import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getDecodedToken, getTenantId } from "../utils/jwtUtils";
import type { RouteAccessContext } from "../utils/routeAccess";
import {
  filterNavEntries,
  getAccessibleWorkspaces,
  isWorkspaceChromePath,
  readStoredWorkspaceId,
  resolveActiveWorkspace,
  workspaceOwnsPath,
  writeStoredWorkspaceId,
  type NavEntry,
  type WorkspaceId,
} from "../utils/workspaces";

function resolveRoles(userRoles?: string[] | null): string[] | undefined {
  if (userRoles?.length) return userRoles;
  return getDecodedToken()?.roles;
}

export function useActiveWorkspace() {
  const { token, user, activeTenantId } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const tenantKey = activeTenantId ?? getTenantId();
  const accessCtx: RouteAccessContext = useMemo(
    () => ({
      roles: resolveRoles(user?.roles),
      hasToken: Boolean(token),
    }),
    [token, user?.roles],
  );

  const available = useMemo(
    () => getAccessibleWorkspaces(accessCtx),
    [accessCtx],
  );

  const [storedId, setStoredId] = useState<WorkspaceId | null>(() =>
    readStoredWorkspaceId(tenantKey),
  );

  useEffect(() => {
    setStoredId(readStoredWorkspaceId(tenantKey));
  }, [tenantKey]);

  const active = useMemo(
    () => resolveActiveWorkspace(location.pathname, available, storedId),
    [location.pathname, available, storedId],
  );

  useEffect(() => {
    if (active && active.id !== storedId) {
      writeStoredWorkspaceId(tenantKey, active.id);
      setStoredId(active.id);
    }
  }, [active, storedId, tenantKey]);

  const navItems: NavEntry[] = useMemo(() => {
    if (!active) return [];
    return filterNavEntries(active.navItems, accessCtx);
  }, [active, accessCtx]);

  const selectWorkspace = useCallback(
    (id: WorkspaceId) => {
      const next = available.find((ws) => ws.id === id);
      if (!next) return;

      writeStoredWorkspaceId(tenantKey, id);
      setStoredId(id);

      const pathname = location.pathname;
      if (isWorkspaceChromePath(pathname) || workspaceOwnsPath(next, pathname)) {
        return;
      }
      navigate(next.homePath);
    },
    [available, tenantKey, location.pathname, navigate],
  );

  return {
    available,
    active,
    navItems,
    selectWorkspace,
    accessCtx,
  };
}
